import { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getTokens } from '@shinetools/sunshine-universal';
import { loadConnectAndInitialize } from '@stripe/connect-js/pure';

import config from 'config';
import useCompanyContext from 'common/hooks/useCompanyContext';

import { StripeConnectSessionDocument } from './graphql/stripeConnectSession.gql';

export const useStripeConnect = () => {
  const { companyProfileId } = useCompanyContext();
  const [queryMerchantAccount, { error, loading }] = useLazyQuery(
    StripeConnectSessionDocument,
  );

  // @see https://docs.stripe.com/connect/get-started-connect-embedded-components#load-and-initialize-connect.js
  const [stripeConnectInstance] = useState(() => {
    const fetchClientSecret = async () => {
      const { data } = await queryMerchantAccount({
        fetchPolicy: 'network-only',
        variables: { companyProfileId: companyProfileId ?? '' },
      });

      if (!data?.viewer.company.merchantAccount) {
        throw new Error('Could not retrieve MerchantAccount');
      }

      return data.viewer.company.merchantAccount.providerSession.clientSecret;
    };

    const { color, radius, size, space } = getTokens();

    return loadConnectAndInitialize({
      appearance: {
        variables: {
          actionPrimaryColorText: color['$grey.800'].val,
          actionSecondaryColorText: color['$grey.700'].val,
          badgeBorderRadius: '24px',
          badgeDangerColorBackground: color['callout.error.background'].val,
          badgeDangerColorBorder: color['callout.error.background'].val,
          badgeDangerColorText: color['callout.error.text'].val,
          badgeNeutralColorBackground:
            color['$button.secondary.background'].val,
          badgeNeutralColorBorder: color['$callout.information.background'].val,
          badgeNeutralColorText: color['$callout.information.text'].val,
          badgeSuccessColorBackground: color['$green.200'].val,
          badgeSuccessColorBorder: color['$green.200'].val,
          badgeSuccessColorText: color['$green.700'].val,
          badgeWarningColorBackground: color['$yellow.200'].val,
          badgeWarningColorBorder: color['$yellow.200'].val,
          badgeWarningColorText: color['$yellow.700'].val,
          bodyMdFontSize: `${size['$text.size.regular'].val}px`,
          bodyMdFontWeight: '400',
          bodySmFontSize: `${size['$text.size.tiny'].val}px`,
          bodySmFontWeight: '400',
          buttonPrimaryColorBackground: color['$button.primary.background'].val,
          buttonPrimaryColorBorder: color['$button.primary.background'].val,
          buttonPrimaryColorText: color.$white.val,
          buttonSecondaryColorBackground:
            color['$button.secondary.background'].val,
          buttonSecondaryColorBorder: color['$button.secondary.background'].val,
          buttonSecondaryColorText: color['$button.primary.background'].val,
          colorBackground: color.$white.val,
          colorBorder: color['$grey.300'].val,
          colorDanger: color['$red.600'].val,
          colorPrimary: color['$grey.800'].val,
          colorSecondaryText: color['$grey.700'].val,
          colorText: color['$grey.800'].val,
          fontFamily: 'Mabry Pro',
          fontSizeBase: `${size['$text.size.regular'].val}px`,
          formBackgroundColor: color.$white.val,
          formBorderRadius: `${radius['$callout.default'].val}px`,
          headingLgFontSize: `${size['$header.size.regular'].val}px`,
          headingLgFontWeight: '500',
          headingMdFontSize: `${size['$text.size.large'].val}px`,
          headingMdFontWeight: '500',
          headingSmFontSize: `${size['$text.size.regular'].val}px`,
          headingSmFontWeight: '500',
          headingXlFontSize: `${size['$header.size.large'].val}px`,
          headingXlFontWeight: '500',
          headingXsFontSize: `${size['$text.size.tiny'].val}px`,
          headingXsFontWeight: '500',
          labelMdFontSize: `${size['$text.size.small'].val}px`,
          labelMdFontWeight: '400',
          labelSmFontSize: `${size['$text.size.tiny'].val}px`,
          labelSmFontWeight: '400',
          offsetBackgroundColor: color['$white'].val,
          spacingUnit: `${space['$space.8'].val}px`,
        },
      },
      fetchClientSecret,
      fonts: [
        {
          display: 'swap',
          family: 'Mabry Pro',
          src: `url(https://storage.googleapis.com/shine-public/fonts/mabry-pro/MabryPro-Regular.woff) format("woff"), url(https://storage.googleapis.com/shine-public/fonts/mabry-pro/MabryPro-Regular.woff2) format("woff2")`,
          style: 'normal',
          weight: 'normal',
        },
        {
          display: 'swap',
          family: 'Mabry Pro',
          src: `url(https://storage.googleapis.com/shine-public/fonts/mabry-pro/MabryPro-Medium.woff) format("woff"), url(https://storage.googleapis.com/shine-public/fonts/mabry-pro/MabryPro-Medium.woff2) format("woff2")`,
          style: 'normal',
          weight: '500',
        },
      ],
      publishableKey: config.stripePublishableKey,
    });
  });

  return { error, loading, stripeConnectInstance };
};
